/**
 * Pure nav item data + the framework-independent buildNavForPrefs function.
 *
 * Keeping these out of useNavItems.ts means tests can import them without
 * pulling in Firebase, React contexts, etc. The hook (useAvailableNavItems)
 * stays in useNavItems.ts and re-exports from here for backward compat.
 */

import { FEATURE_REGISTRY, findPrimaryFeature } from "./featureRegistry";
import type { OnboardingPreferences } from "../types";

export type NavItem = {
  id: string;
  label: string;
  icon: string;
  path: string;
};

export const ALL_NAV_ITEMS: NavItem[] = [
  { id: "notes", label: "Notes", icon: "fa-solid fa-note-sticky", path: "/notes/" },
  { id: "tasks", label: "Tasks", icon: "fa-solid fa-list-check", path: "/tasks/" },
  { id: "calendar", label: "Calendar", icon: "fa-solid fa-calendar-days", path: "/calendar/" },
  { id: "health", label: "Health", icon: "fa-solid fa-heart-pulse", path: "/health" },
  { id: "fitness", label: "Fitness", icon: "fa-solid fa-dumbbell", path: "/fitness" },
  { id: "running", label: "Running", icon: "fa-solid fa-person-running", path: "/running" },
  { id: "womens-health", label: "Women's", icon: "fa-solid fa-venus", path: "/womens-health" },

  { id: "collection", label: "Collection", icon: "fa-solid fa-layer-group", path: "/collection" },
  { id: "pet", label: "Pet", icon: "fa-solid fa-paw", path: "/pet" },
  { id: "badges", label: "Badges", icon: "fa-solid fa-award", path: "/badges" },
  { id: "community", label: "Community", icon: "fa-solid fa-users", path: "/community" },
  { id: "family", label: "Family & Friends", icon: "fa-solid fa-people-group", path: "/family" },
  { id: "account", label: "Account", icon: "fa-solid fa-user", path: "/account" },
  { id: "tip-us", label: "Tip Us", icon: "fa-solid fa-heart", path: "/tip-us" },
];

export const DEFAULT_NAV_BAR_ITEMS = ["notes", "tasks", "calendar", "community"];

/**
 * Build a nav bar item list based on the user's primary focus and preferences.
 * The primary focus page goes first, then community, then enabled extras
 * pulled from FEATURE_REGISTRY in priority order. All decisions about which
 * features qualify live in the registry — this function is just an ordering
 * policy.
 */
export function buildNavForPrefs(prefs: Partial<OnboardingPreferences>): string[] {
  const items: string[] = [];
  const fullPrefs = prefs as OnboardingPreferences;

  // 1. Primary slot — derived from primaryUseCase via the registry
  const primary = findPrimaryFeature(prefs.primaryUseCase ?? null);
  if (primary) {
    items.push(primary.navItem);
  } else {
    // Fallback: if the user opted into notes/tasks, surface them; otherwise
    // leave the primary slot empty and let the extras pass fill from features.
    if (prefs.notes) items.push("notes");
    if (prefs.tasks) items.push("tasks");
  }

  // 2. Community always present
  if (!items.includes("community")) items.push("community");

  // 3. Pad to ~5 with enabled features from the registry, in registry order
  for (const feature of FEATURE_REGISTRY) {
    if (items.length >= 5) break;
    if (items.includes(feature.navItem)) continue;
    if (!feature.isEnabled(fullPrefs)) continue;
    items.push(feature.navItem);
  }

  // 4. Final fallback — drop badges in if we still have room
  if (items.length < 5 && !items.includes("badges")) {
    items.push("badges");
  }

  return items;
}
